/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ExternalLinkIcon, XIcon } from 'lucide-react';
import moment from 'moment';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { cn } from '@/lib/utils';

const CONTROL_STATUS = {
  ACTIVE: 1,
  CANCELED: 2,
};

ControlDetailsPreview.propTypes = {
  control: PropTypes.object.isRequired,
  onViewFullDetails: PropTypes.func,
  onCancel: PropTypes.func,
  controllerColors: PropTypes.object,
  mainColor: PropTypes.string,
  inline: PropTypes.bool,
  isOverdue: PropTypes.bool,
};

/**
 * Control Details Preview for Betterfield
 *
 * Renders an inspection summary either inside the calendar's
 * control-details modal (default) or inline inside an expanded
 * day-events list row (`inline=true`, more compact). All chrome is
 * Tailwind + shadcn now — the previous styled() Typography helpers
 * (`Label`, `Validation`) are gone in favour of conditional Tailwind
 * classes (`text-muted-foreground` for labels,
 * `text-emerald-600`/`sky-600`/`destructive` for the conformity
 * state).
 */
export default function ControlDetailsPreview({
  control,
  onViewFullDetails,
  onCancel,
  controllerColors = {},
  // Kept for caller compatibility; the chrome now follows the shadcn
  // theme tokens (Button variant="default" reads the --primary token).
  // eslint-disable-next-line no-unused-vars
  mainColor,
  inline = false,
  isOverdue = false,
}) {
  const { t } = useTranslation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleCancelClick = (e) => {
    if (e) e.stopPropagation();
    setConfirmDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    setConfirmDialogOpen(false);
    if (onCancel) onCancel();
  };

  const controllerName = control.controller
    ? `${control.controller.firstName} ${control.controller.lastName}`
    : t('calendar.unassigned');

  const controllerColor = control.controller
    ? controllerColors[control.controller.id] || '#757575'
    : '#757575';

  const formatDate = (dateTime, date) => {
    if (dateTime) return moment(dateTime).format('DD/MM/YYYY HH:mm');
    if (date) return moment(date).format('DD/MM/YYYY HH:mm');
    return 'N/A';
  };

  const getConformityLabel = (conformity) => {
    if (conformity === true || conformity === 1) {
      return t('inspections.info-card.complaint');
    }
    if (conformity === 2) {
      return t('inspections.items.scheduled');
    }
    return t('inspections.info-card.non-complaint');
  };

  const getConformityClass = (conformity) => {
    if (conformity === true || conformity === 1) {
      return 'text-emerald-600 dark:text-emerald-400 font-medium';
    }
    if (conformity === 2) {
      return 'text-sky-600 dark:text-sky-400 font-medium';
    }
    return 'text-destructive font-medium';
  };

  const isCanceled = control.status === CONTROL_STATUS.CANCELED;

  const labelClass = cn(
    'text-muted-foreground',
    inline ? 'text-xs' : 'text-sm',
  );
  const valueClass = inline ? 'text-sm' : 'text-base';

  // Tailwind doesn't ship a margin-bottom utility named after the
  // "spacing" prop the MUI version carried; collapse to one of two
  // classes keyed on `inline`.
  const rowGap = inline ? 'mb-2' : 'mb-3';

  return (
    <div className={inline ? 'pt-2' : ''}>
      {(isCanceled || isOverdue) ? (
        <div className={cn('flex gap-2', rowGap)}>
          {isCanceled ? (
            <Badge variant="secondary">{t('calendar.status.canceled')}</Badge>
          ) : isOverdue ? (
            <Badge variant="destructive">{t('calendar.status.late')}</Badge>
          ) : null}
        </div>
      ) : null}

      <div className={cn('grid gap-3', !inline && 'gap-6', 'md:grid-cols-2')}>
        <div>
          <div className={rowGap}>
            <p className={labelClass}>
              {`${t('inspections.items.date')}:`}
            </p>
            <p className={valueClass}>
              {formatDate(control.dateTime, control.date)}
            </p>
          </div>

          <div className={rowGap}>
            <p className={labelClass}>
              {`${t('inspections.info-card.employee')}:`}
            </p>
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className={cn(
                  'inline-block rounded-sm',
                  inline ? 'size-3' : 'size-3.5',
                )}
                style={{ backgroundColor: controllerColor }}
              />
              <span className={valueClass}>{controllerName}</span>
            </div>
          </div>

          <div className={rowGap}>
            <p className={labelClass}>
              {`${t('inspections.info-card.state')}:`}
            </p>
            <p className={cn(valueClass, getConformityClass(control.conformity))}>
              {getConformityLabel(control.conformity)}
            </p>
          </div>
        </div>

        <div>
          {control.resource ? (
            <div className={rowGap}>
              <p className={labelClass}>
                {`${t('inspections.items.resource')}:`}
              </p>
              <p className={valueClass}>{control.resource.name || 'N/A'}</p>
            </div>
          ) : null}

          {control.resource?.resourceType ? (
            <div className={rowGap}>
              <p className={labelClass}>
                {`${t('inspections.items.resource-type')}:`}
              </p>
              <p className={valueClass}>
                {control.resource.resourceType.name || 'N/A'}
              </p>
            </div>
          ) : null}

          {control.flow ? (
            <div className={rowGap}>
              <p className={labelClass}>
                {`${t('inspections.items.flow-type')}:`}
              </p>
              <p className={valueClass}>{control.flow.name || 'N/A'}</p>
            </div>
          ) : null}

          {control.location ? (
            <div className={rowGap}>
              <p className={labelClass}>
                {`${t('calendar.day-events.location')}:`}
              </p>
              <p className={valueClass}>{control.location.name || 'N/A'}</p>
            </div>
          ) : null}
        </div>
      </div>

      {inline ? (
        <div className="mt-2 flex justify-end gap-2">
          {isOverdue && !isCanceled && onCancel ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={handleCancelClick}
            >
              <XIcon className="size-4" />
              {t('calendar.control-details.cancel')}
            </Button>
          ) : null}
          {onViewFullDetails ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onViewFullDetails();
              }}
            >
              {t('calendar.control-details.view-full-details')}
              <ExternalLinkIcon className="size-4" />
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <hr className="my-4 border-border" />
          <div className="flex justify-end gap-2">
            {isOverdue && !isCanceled && onCancel ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelClick}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <XIcon className="size-4" />
                {t('calendar.control-details.cancel')}
              </Button>
            ) : null}
            {onViewFullDetails ? (
              <Button type="button" onClick={onViewFullDetails}>
                {t('calendar.control-details.view-full-details')}
                <ExternalLinkIcon className="size-4" />
              </Button>
            ) : null}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmDialogOpen}
        onOpenChange={(next) => {
          if (!next) setConfirmDialogOpen(false);
        }}
        title={t('calendar.cancel.confirm.title')}
        description={t('calendar.cancel.confirm.message')}
        confirmLabel={t('common.yes')}
        cancelLabel={t('common.no')}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
}
